class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: "quick-view--open",
      opening: "quick-view--opening",
      closing: "quick-view--closing",
    });

    this.addEventListener("close", () => {
      const drawerContent = this.querySelector(".quick-view__content");
      drawerContent.innerHTML = "";
      drawerContent.classList.remove("hide-cover");

      this.returnToOrigin();

      document.dispatchEvent(new CustomEvent("quickview:close"));
    });
  }

  /*
    A card can sit inside an ancestor that establishes a containing block for
    fixed positioning — a Swiper carousel track carries a `transform`, for
    example. That ancestor also opens its own stacking context, so the modal
    would centre on the track instead of the viewport and paint underneath the
    page overlay. Move the drawer to <body> while it is open, and put it back
    afterwards so the card keeps its hover trigger and its DOM position.
  */
  static isTrapped(element) {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
      const styles = getComputedStyle(node);
      if (
        styles.transform !== "none" ||
        styles.filter !== "none" ||
        styles.perspective !== "none" ||
        styles.contain.includes("paint") ||
        styles.contain.includes("layout") ||
        /transform|filter|perspective/.test(styles.willChange)
      ) {
        return true;
      }
    }
    return false;
  }

  portalToBody() {
    if (this.quickViewPlaceholder || !QuickViewDrawer.isTrapped(this)) return;

    const placeholder = document.createComment("quick-view-drawer");
    this.parentNode.insertBefore(placeholder, this);
    this.quickViewPlaceholder = placeholder;
    document.body.appendChild(this);
    // Pins the drawer to the viewport. Without it the drawer is a static box at
    // the end of <body>, so its absolutely positioned <summary> sits at the top
    // of the document and the focus trap scrolls the page up to reach it.
    this.classList.add("quick-view-drawer--portalled");
  }

  returnToOrigin() {
    const placeholder = this.quickViewPlaceholder;
    this.quickViewPlaceholder = null;
    if (!placeholder) return;

    this.classList.remove("quick-view-drawer--portalled");
    if (placeholder.parentNode) placeholder.parentNode.insertBefore(this, placeholder);
    placeholder.remove();
  }

  // The section is fetched only once the drawer opens, so show a spinner rather
  // than an empty (zero-height) modal box over the dimmed page.
  showLoading() {
    const drawerContent = this.querySelector(".quick-view__content");
    if (drawerContent && !drawerContent.firstElementChild) {
      drawerContent.innerHTML = '<div class="quick-view__loading"><span class="quick-view__spinner"></span></div>';
    }
  }

  openMenuDrawer(summaryElement = false) {
    this.portalToBody();
    this.showLoading();
    super.openMenuDrawer(summaryElement);
  }

  // App-extension Notify Me is appended to <body>, not inside this drawer.
  isNotifyMePopup(target) {
    return Boolean(target?.closest?.("#notify-me-popup"));
  }

  onBodyClick(event) {
    if (this.isNotifyMePopup(event.target)) return;
    super.onBodyClick(event);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.isNotifyMePopup(document.activeElement)) return;
      super.onFocusOut();
    });
  }
}
customElements.define("quick-view-drawer", QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener("click", () => {
      const wrapper = this.closest(".card-wrapper");
      const drawer = wrapper.querySelector("quick-view-drawer");
      if (drawer) {
        drawer.querySelector("summary").click();

        document.dispatchEvent(
          new CustomEvent("quickview:open", {
            detail: {
              productUrl: this.dataset.productUrl,
            },
          }),
        );
      } else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define("quick-view-button", QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Re-parenting (see QuickViewDrawer.portalToBody) re-runs this callback, so
    // keep a single observer rather than stacking one up per move.
    if (this.quickViewObserver) return;

    this.quickViewObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    this.quickViewObserver.observe(this);
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = ".quick-view__content";
    const drawerContent = this.querySelector(selector);
    const productUrl = this.dataset.productUrl.split("?")[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then((response) => response.text())
      .then((responseText) => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, "text/html");
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          this.bindWishlist(drawerContent);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 200);

        setTimeout(() => {
          drawerContent.classList.add("hide-cover");

          document.dispatchEvent(
            new CustomEvent("quickview:loaded", {
              detail: {
                productUrl: this.dataset.productUrl,
              },
            }),
          );
        }, 500);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  bindWishlist(drawerContent) {
    drawerContent.addEventListener(
      "click",
      (event) => {
        const heart = event.target.closest(".product__wishlist");
        if (!heart) return;

        event.preventDefault();
        event.stopPropagation();

        const productId = drawerContent.querySelector("product-info")?.dataset.productId;
        const cardButton = productId
          ? document.querySelector(`.sb-card-wrapper[data-product-id="${productId}"] .universal-wishlist-btn`)
          : null;
        const wishlistButton = cardButton || document.querySelector("#wishlist-icon-btn");
        wishlistButton?.click();
      },
      true,
    );
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define("quick-view", QuickView);
